import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Row, Col, Container } from 'reactstrap';
import { GoogleMap, Polygon, Marker } from '@react-google-maps/api';
import { _getZonedSchools, _getNearbySchools } from '../../../../shared/helpers/apicalls';
import {
	_capitalizeText,
	_formatLatLong,
	_formatCoordsMulti,
} from '../../../../shared/helpers/utils';
import LinearLoading from '../../../../shared/components/LinearLoading';

const mapStyles = [
	{ stylers: [{ hue: "#009ECB" }, { saturation: -60 }] },
	{ featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
	{ featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
];

const map_marker = {
	path: "M242.606,0C142.124,0,60.651,81.473,60.651,181.955s151.631,303.257,181.956,303.257c30.326,0,181.955-202.775,181.955-303.257S343.089,0,242.606,0zM242.606,303.257c-66.9,0-121.302-54.433-121.302-121.302S175.706,60.651,242.606,60.651c66.902,0,121.302,54.435,121.302,121.304S309.509,303.257,242.606,303.257z",
	fillColor: "#376ee2",
	fillOpacity: 0.7,
	strokeWeight: 1,
	scale: 0.08,
};

const polygonOptions = {
	fillColor: "#376EE2",
	fillOpacity: 0.5,
	strokeColor: "#376EE2",
	strokeOpacity: 1,
	strokeWeight: 1,
};

const SchoolZoneMap = ({ lat, long, polyCoords }) => {
	const containerStyle = { width: '100%', height: '240px' };

	const onMapLoad = useCallback((map) => {
		if (polyCoords && polyCoords.length > 0 && window.google) {
			const bounds = new window.google.maps.LatLngBounds();
			polyCoords.forEach(coord => {
				bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
			});
			map.fitBounds(bounds);
		}
	}, [polyCoords]);

	return (
		<GoogleMap
			mapContainerStyle={containerStyle}
			center={{ lat, lng: long }}
			zoom={15}
			options={{ styles: mapStyles, scrollwheel: false }}
			onLoad={onMapLoad}
		>
			{polyCoords &&
				<Polygon
					paths={polyCoords}
					options={polygonOptions}
				/>
			}
			<Marker
				position={{ lat, lng: long }}
				icon={map_marker}
			/>
		</GoogleMap>
	);
};

const formatSchoolsList = (schoolZone) => {
	if (schoolZone && schoolZone.schools && Array.isArray(schoolZone.schools)) {
		return schoolZone.schools.map((school, i) => {
			const code = schoolZone.dbn && schoolZone.dbn[i] ? schoolZone.dbn[i].slice(2) : false;
			if (code) {
				return (
					<div key={school + '-' + i}>
						<a href={`https://www.schools.nyc.gov/schools/${code}`} target="_blank" rel="noopener noreferrer">
							{_capitalizeText(school)} <i className="fa fa-external-link" style={{ fontSize: 10 }}></i>
						</a>
					</div>
				);
			}
			return <div key={school + '-' + i}>{_capitalizeText(school)}</div>;
		});
	}
	return '--';
};

const ZonedSchoolWithMap = ({ label, zone, latLong }) => {
	if (!zone) return null;
	const polyCoords = zone.geojson ? _formatCoordsMulti(zone.geojson)[0] : false;
	return (
		<div className="mb-4">
			<div className="font-weight-bold mb-2" style={{ fontSize: 14 }}>{label}</div>
			{latLong &&
				<Fragment>
					<SchoolZoneMap
						lat={latLong.lat}
						long={latLong.long}
						polyCoords={polyCoords}
					/>
					<p className="text-center mt-1" style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>Map shows school zone boundaries</p>
				</Fragment>
			}
			<Row style={{ fontSize: 12 }} className="mb-1">
				<Col sm={4} xs={4}>School District</Col>
				<Col sm={8} xs={8} className="font-weight-bold">
					{zone.school_district || '--'}
				</Col>
			</Row>
			<Row style={{ fontSize: 12 }} className="mb-1">
				<Col sm={4} xs={4}>Zone School</Col>
				<Col sm={8} xs={8} className="font-weight-bold">
					{formatSchoolsList(zone) !== '--' ? formatSchoolsList(zone) : '--'}
				</Col>
			</Row>
			<Row style={{ fontSize: 12 }} className="mb-1">
				<Col sm={4} xs={4}>DBN</Col>
				<Col sm={8} xs={8} className="font-weight-bold">
					{zone.dbn ? (Array.isArray(zone.dbn) ? zone.dbn.join(', ') : zone.dbn) : '--'}
				</Col>
			</Row>
			{zone.remarks &&
				<Row style={{ fontSize: 12 }} className="mb-1">
					<Col sm={4} xs={4}>Remarks</Col>
					<Col sm={8} xs={8} className="font-weight-bold">{zone.remarks}</Col>
				</Row>
			}
		</div>
	);
};

const NearbySchoolList = ({ label, schools }) => {
	if (!schools || schools.length === 0) return null;
	return (
		<div className="mb-3">
			<div className="font-weight-bold mb-1" style={{ fontSize: 14 }}>{label}</div>
			{schools.map(school => (
				<div className="c_school-line" key={school.location_name} style={{ padding: '6px 0' }}>
					<Row style={{ fontSize: 12 }}>
						<Col sm={9} xs={8} className="font-weight-bold">
							<a href={`https://www.google.com/maps/place/${school.primary_address},${school.zip}`} target="_blank" rel="noopener noreferrer">
								{school.location_name}
							</a>
						</Col>
						<Col sm={3} xs={4} style={{ textAlign: 'right' }}>
							{school.distance} mi
						</Col>
					</Row>
					{school.grades && Array.isArray(school.grades) &&
						<Row style={{ fontSize: 12 }}>
							<Col xs={3}>Grades:</Col>
							<Col xs={9}>
								<ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap' }}>
									{school.grades.map((grade, i) =>
										<li key={school.location_name + '-' + grade + '-' + i} style={{
											display: 'inline-block', padding: '1px 4px', background: '#92afec',
											fontSize: 11, color: '#fff', lineHeight: '18px', marginRight: 3, marginBottom: 3,
										}}>{grade}</li>
									)}
								</ul>
							</Col>
						</Row>
					}
				</div>
			))}
		</div>
	);
};

const Schools = (props) => {
	const { buildingRes } = props;
	const [schoolZones, setSchoolZones] = useState(null);
	const [nearbySchools, setNearbySchools] = useState(null);
	const [loading, setLoading] = useState(true);

	const latLong = buildingRes && buildingRes.geocode ? _formatLatLong(buildingRes.geocode) : false;

	useEffect(() => {
		if (!buildingRes || !buildingRes.geocode) {
			setLoading(false);
			return;
		}
		let mounted = true;
		const fetchData = async () => {
			const [zoneData, nearbyData] = await Promise.all([
				_getZonedSchools(buildingRes),
				_getNearbySchools(buildingRes),
			]);
			if (mounted) {
				setSchoolZones(zoneData);
				setNearbySchools(nearbyData);
				setLoading(false);
			}
		};
		fetchData();
		return () => { mounted = false; };
	}, [buildingRes && buildingRes.geocode]);

	if (loading) return <LinearLoading />;

	const hasZones = schoolZones && Object.keys(schoolZones).length > 0;
	const hasNearby = nearbySchools && Object.keys(nearbySchools).length > 0;

	if (!hasZones && !hasNearby) {
		return (
			<Container className="dashboard" style={{ border: "0px solid #DBE0E8" }}>
				<div style={{ fontSize: 12, color: '#999' }}>No school data available.</div>
			</Container>
		);
	}

	return (
		<Container className="dashboard" style={{ border: "0px solid #DBE0E8" }}>
			<p style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>The following shows zoned schools and schools nearby.</p>

			{hasZones && ['elementary', 'middle', 'high'].map((type) => {
				const zone = schoolZones[type];
				if (!zone) return null;
				const labelMap = { elementary: 'Zoned Elementary School', middle: 'Zoned Middle School', high: 'Zoned High School' };
				const nearbyKey = { elementary: 'elementary', middle: 'junior_high-intermediate-middle', high: 'high_school' };
				const nearbyLabel = { elementary: 'Elementary Schools Nearby', middle: 'Middle Schools Nearby', high: 'High Schools Nearby' };
				return (
					<Fragment key={type}>
						<Row>
							<Col sm={7} xs={12}>
								<ZonedSchoolWithMap label={labelMap[type]} zone={zone} latLong={latLong} />
							</Col>
							<Col sm={5} xs={12}>
								{hasNearby && nearbySchools[nearbyKey[type]] &&
									<NearbySchoolList label={nearbyLabel[type]} schools={nearbySchools[nearbyKey[type]]} />
								}
							</Col>
						</Row>
						{type !== 'high' && <hr />}
					</Fragment>
				);
			})}

			{hasNearby && !hasZones &&
				<Row>
					<Col sm={12}>
						<NearbySchoolList label="Elementary Schools Nearby" schools={nearbySchools['elementary']} />
						<NearbySchoolList label="Middle Schools Nearby" schools={nearbySchools['junior_high-intermediate-middle']} />
						<NearbySchoolList label="High Schools Nearby" schools={nearbySchools['high_school']} />
					</Col>
				</Row>
			}
		</Container>
	);
};

export default Schools;
